import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { StoreThemeJson } from "@a2z/storefront-schemas";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { endpoints } from "@/lib/api/endpoints";

/** Logo brand-color extraction metadata, surfaced so the editor can offer a
 *  "your brand" theme. `null` when the store never extracted brand colors. */
export interface BrandExtraction {
  palette: string[];
  accent?: string | null;
  theme?: string | null;
  source?: string | null;
  extractedAt?: string | null;
}

/**
 * The handover lock as the server sends it, on the theme payload.
 *
 * Declared HERE, with the view it rides on, rather than in the editor's
 * `designLock.ts`: this is the wire shape, and `shared/` may not reach into a
 * module. The editor's `resolveDesignLock` — the POLICY over this shape — stays
 * where the editor keeps it and imports the type from here.
 *
 * OPTIONAL ON PURPOSE, mirroring the server type: read it through
 * `resolveDesignLock`, never by reaching for `.isFrozen` directly, so an older
 * cached payload degrades to "not locked" instead of throwing.
 */
export interface DesignLockPayload {
  isFrozen: boolean;
  frozenAt: string | null;
  /** True only for the store OWNER: the lock's authority is ownership, never a
   *  grantable permission. */
  canManage: boolean;
}

/** View shape returned by `GET /api/seller/theme`. */
export interface StoreThemeView {
  storeId: string;
  schemaVersion: number;
  live: StoreThemeJson;
  draft: StoreThemeJson | null;
  hasDraft: boolean;
  publishedAt: string | null;
  draftedAt: string | null;
  /** The gallery theme this store last applied (+ its version), or null if it
   *  never applied one. Written at APPLY time, so it describes the draft —
   *  with `hasDraft` it tells "applied and live" from "not published yet". */
  appliedThemeId: string | null;
  appliedThemeVersion: number | null;
  /** The theme buyers are actually served. Differs from the applied pair only
   *  while a theme has been applied but not published. */
  publishedThemeId: string | null;
  publishedThemeVersion: number | null;
  brandExtraction: BrandExtraction | null;
  /**
   * The handover lock, riding the payload the editor already loads so the
   * restriction is legible before the first edit (Rule 16).
   */
  designLock?: DesignLockPayload;
}

/** Exported so the brand mutation, which lives beside this, can replace the
 *  cached view with the server's own answer after a live brand write. */
export const THEME_QUERY_KEY = ["seller", "theme"] as const;

/**
 * Load the seller's theme (live + draft) for the editor.
 *
 * `enabled` exists for readers OUTSIDE the editor. `GET /seller/theme` is gated
 * on `DESIGN_VIEW` now, so a member without it takes a 403 — and this query
 * carries no `skipGlobalErrorToast`, which would turn a settings screen they
 * are entitled to into an error toast about a theme they never asked for. Such
 * callers pass their own permission check here and fall back. The editor itself
 * never needs it: its own nav entry is gated on the same key.
 */
export function useThemeQuery(options: { enabled?: boolean } = {}) {
  return useQuery<StoreThemeView>({
    queryKey: THEME_QUERY_KEY,
    queryFn: () => apiJsonClient<StoreThemeView>(endpoints.seller.theme),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
    // A tab left open since yesterday holds a stale server copy. The shell
    // deliberately hydrates from this ONCE (re-hydrating mid-edit would throw
    // away unsaved work), so this does not move the editor on its own — it
    // keeps the cache honest for everything else that reads it, and pairs
    // with the cross-tab notice in `lib/editorPresence.ts`.
    refetchOnWindowFocus: true,
  });
}

/** Persist the current draft to the backend. */
export function useUpdateDraftMutation() {
  const queryClient = useQueryClient();
  return useMutation<StoreThemeView, Error, StoreThemeJson>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (draft) =>
      apiJsonClient<StoreThemeView>(endpoints.seller.themeDraft, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(draft),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(THEME_QUERY_KEY, data);
    },
  });
}

/**
 * Publish the current draft → live, with an optional one-line note describing
 * what changed. The backend has always accepted and stored a 280-char `note`
 * on this endpoint; this mutation was typed `void` and posted no body, which
 * is why every row in the publish history was a bare timestamp.
 */
export function usePublishThemeMutation() {
  const queryClient = useQueryClient();
  return useMutation<StoreThemeView, Error, { note?: string } | void>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (vars) =>
      apiJsonClient<StoreThemeView>(endpoints.seller.themePublish, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(vars?.note ? { note: vars.note } : {}),
      }),
    onSuccess: async (data) => {
      queryClient.setQueryData(THEME_QUERY_KEY, data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["seller", "theme", "publications"] }),
        queryClient.invalidateQueries({ queryKey: ["seller", "theme", "saved-looks"] }),
      ]);
    },
  });
}

/**
 * Lock / unlock the published design — the owner's handover control.
 *
 * There is no permission to check on the client, because there is no permission
 * to hold: the backend asserts `Store.sellerId`, and the editor knows whether
 * to offer the control from `designLock.canManage` on the payload it already
 * has. Both routes answer with the full theme view, so the lock state in the
 * cache is replaced by the server's own answer rather than guessed at.
 */
export function useSetDesignFreezeMutation() {
  const queryClient = useQueryClient();
  return useMutation<StoreThemeView, Error, boolean>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: (frozen) =>
      apiJsonClient<StoreThemeView>(
        frozen ? endpoints.seller.themeFreeze : endpoints.seller.themeUnfreeze,
        { method: "POST" },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(THEME_QUERY_KEY, data);
    },
  });
}

// ---------------------------------------------------------------------------
// Phase 6 — version history / audit log
// ---------------------------------------------------------------------------

export interface PublicationListItem {
  id: string;
  schemaVersion: number;
  sellerId: string | null;
  note: string | null;
  publishedAt: string;
}

const PUBLICATIONS_QUERY_KEY = ["seller", "theme", "publications"] as const;

/** List recent publications for the editor's history panel. Disabled by
 *  default; the panel calls `refetch` when it opens to avoid loading the
 *  log on every editor mount. */
export function usePublicationsQuery(enabled: boolean) {
  return useQuery<{ items: PublicationListItem[] }>({
    queryKey: PUBLICATIONS_QUERY_KEY,
    queryFn: () =>
      apiJsonClient<{ items: PublicationListItem[] }>(
        endpoints.seller.themePublications,
      ),
    staleTime: 30_000,
    enabled,
  });
}

/** Restore a prior publication into the current draft. The seller still
 *  has to publish from the normal flow afterward — restore is staged, not
 *  applied. */
export function useRestorePublicationMutation() {
  const queryClient = useQueryClient();
  return useMutation<StoreThemeView, Error, string>({
    mutationFn: (publicationId) =>
      apiJsonClient<StoreThemeView>(
        endpoints.seller.themePublicationRestore(publicationId),
        { method: "POST" },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(THEME_QUERY_KEY, data);
    },
  });
}

/** Stage the store's original (creation-time) design into the draft. Live is
 *  untouched; the seller reviews and publishes through the normal flow. */
export function useResetToDefaultMutation() {
  const queryClient = useQueryClient();
  return useMutation<StoreThemeView, Error, void>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: () =>
      apiJsonClient<StoreThemeView>(endpoints.seller.themeReset, {
        method: "POST",
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(THEME_QUERY_KEY, data);
    },
  });
}

/** Discard the current draft, leaving live untouched. */
export function useDiscardDraftMutation() {
  const queryClient = useQueryClient();
  return useMutation<StoreThemeView, Error, void>({
    meta: { skipGlobalErrorToast: true },
    mutationFn: () =>
      apiJsonClient<StoreThemeView>(endpoints.seller.themeDiscard, {
        method: "POST",
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(THEME_QUERY_KEY, data);
    },
  });
}
